import { useMemo } from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';

import { OrganisationUsage } from '../../../../api/types/backend-dto-types';
import { Badge } from '../../../../components/atoms/Badge';
import TooltipIcon from '../../../../components/atoms/TooltipIcon';

export const TotalUsage = ({ periodCreditUsage }: { periodCreditUsage: OrganisationUsage['periodCreditUsage'] }) => {
  const { i18n } = useTranslation();

  const totalCreditsUsed = useMemo(() => {
    if (!periodCreditUsage) return 0;
    return periodCreditUsage.billable + periodCreditUsage.nonBillable;
  }, [periodCreditUsage]);

  const billableUsagePercentage = useMemo(() => {
    if (!periodCreditUsage) return null;
    const total = periodCreditUsage.maxCredits ?? totalCreditsUsed;
    return (periodCreditUsage.billable / total) * 100;
  }, [periodCreditUsage]);

  const nonBillableUsagePercentage = useMemo(() => {
    if (!periodCreditUsage) return null;
    const total = periodCreditUsage.maxCredits ?? totalCreditsUsed;
    return (periodCreditUsage.nonBillable / total) * 100;
  }, [periodCreditUsage]);

  if (periodCreditUsage && billableUsagePercentage !== null && nonBillableUsagePercentage !== null)
    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-800">Total Credit Usage</h1>
          {periodCreditUsage.isUsingTrialPackage && (
            <Badge variant="secondary" className="bg-teal-100 text-teal-700">
              Onboarding Package
            </Badge>
          )}
        </div>

        <p className="mt-1 text-sm text-gray-600">
          Credit usage for the{' '}
          {periodCreditUsage.isUsingTrialPackage
            ? 'onboarding period.'
            : 'current billing period across your entire organisation.'}
        </p>

        <div className="relative mt-3">
          <div className="mb-2 flex items-end justify-between">
            <p className="text-xl font-semibold">
              <span className="inline-flex items-center gap-2">
                <span
                  className={
                    !periodCreditUsage.isUsingTrialPackage &&
                    periodCreditUsage.maxCredits &&
                    totalCreditsUsed > periodCreditUsage.maxCredits
                      ? 'text-red-600'
                      : ''
                  }
                >
                  {new Intl.NumberFormat(i18n.language).format(totalCreditsUsed)}
                </span>

                {periodCreditUsage.isUsingTrialPackage && periodCreditUsage.maxCredits && (
                  <TooltipIcon
                    side="right"
                    triggerClassName="text-teal-500"
                    tooltipContent={`The onboarding package has generous limits on usage. Currently this is set to ${new Intl.NumberFormat(
                      'en-US',
                      { notation: 'compact' },
                    ).format(
                      periodCreditUsage.maxCredits,
                    )} credits. If you feel this is not enough for your use case, reach out to support@wexler.ai.`}
                  />
                )}
              </span>
              {!periodCreditUsage?.isUsingTrialPackage && periodCreditUsage?.maxCredits && (
                <>
                  <span className="inline-flex items-center gap-1.5 pl-1 text-base text-gray-500">
                    {' '}
                    / {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(
                      periodCreditUsage.maxCredits,
                    )}{' '}
                    limit
                    <TooltipIcon
                      tooltipContent={`${((totalCreditsUsed / periodCreditUsage.maxCredits) * 100).toFixed(1)}%`}
                    />
                  </span>
                </>
              )}
            </p>

            {periodCreditUsage?.endDate && (
              <p className="text-sm text-gray-500">
                usage resets on{' '}
                <strong>{new Intl.DateTimeFormat(i18n.language).format(new Date(periodCreditUsage.endDate))}</strong>
              </p>
            )}
          </div>

          {/* Absolutely positioned on top of the below bar to show two both values (if we decide to do that) */}
          {/* Adding both billable and non-billable */}
          <ProgressBar
            completed={billableUsagePercentage + nonBillableUsagePercentage}
            height={'10px'}
            bgColor="var(--color-brandSecondary)"
            baseBgColor={periodCreditUsage.isUsingTrialPackage ? 'var(--color-teal-100)' : undefined}
            className="w-full"
            isLabelVisible={false}
            animateOnRender={true}
          />

          {/* Non-billable sections and legend removed while non-billable not in use */}
          {/* <ProgressBar
            completed={billableUsagePercentage + nonBillableUsagePercentage}
            height={'10px'}
            bgColor="#349989"
            isLabelVisible={false}
            animateOnRender={true}
          /> */}

          <div className="mt-3 flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="bg-brand-secondary h-3 w-3 rounded-sm"></div>
              <span className="text-gray-500">Credits used</span>
            </div>
            {/* <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#349989' }}></div>
              <span className="text-gray-500">Non-billable</span>
            </div> */}
          </div>
        </div>
      </div>
    );
};
